import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { CheckCircle, Loader2, XCircle, Zap } from 'lucide-react';
import axios from 'axios';

// Détection automatique du domaine pour la production
const getApiUrl = () => {
  if (typeof window !== 'undefined') {
    if (window.location.hostname === 'boost-strategia.com' || window.location.hostname === 'www.boost-strategia.com') {
      return 'https://boost-strategia.com';
    }
  }
  return process.env.REACT_APP_BACKEND_URL || '';
};
const API = `${getApiUrl()}/api`;

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('loading'); // loading, success, error
  const [attempts, setAttempts] = useState(0);
  const navigate = useNavigate();
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/auth');
      return;
    }

    if (!sessionId) {
      navigate('/dashboard');
      return;
    }

    checkPaymentStatus();
  }, [sessionId, navigate]);

  const checkPaymentStatus = async () => {
    const token = localStorage.getItem('token');
    const maxAttempts = 5;

    if (attempts >= maxAttempts) {
      setStatus('error');
      return;
    }

    try {
      const response = await axios.get(
        `${API}/payments/checkout/status/${sessionId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.status === 'paid') {
        setStatus('success');
      } else if (response.data.status === 'expired') {
        setStatus('error');
      } else {
        // Continue polling
        setAttempts(prev => prev + 1);
        setTimeout(() => checkPaymentStatus(), 2000);
      }
    } catch (error) {
      console.error('Status check error:', error);
      setStatus('error');
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-6">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2" data-testid="logo-link">
            <div className="w-12 h-12 bg-primary rounded-md flex items-center justify-center">
              <Zap className="w-7 h-7 text-black" />
            </div>
            <span className="text-3xl font-heading font-bold">Boost-StrategIA</span>
          </Link>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          <Card className="p-8 border-border text-center">
            {status === 'loading' && (
              <div data-testid="payment-loading">
                <Loader2 className="w-16 h-16 text-primary mx-auto mb-4 animate-spin" />
                <h2 className="text-2xl font-heading font-semibold mb-2">Vérification du paiement...</h2>
                <p className="text-muted">Veuillez patienter quelques instants</p>
              </div>
            )}

            {status === 'success' && (
              <div data-testid="payment-success">
                <CheckCircle className="w-16 h-16 text-primary mx-auto mb-4" />
                <h2 className="text-2xl font-heading font-semibold mb-2">Paiement réussi !</h2>
                <p className="text-muted mb-6">
                  Votre abonnement a été activé avec succès. Vous pouvez maintenant accéder à toutes vos études.
                </p>
                <Button
                  onClick={() => navigate('/dashboard')}
                  className="shadow-glow-md"
                  data-testid="go-to-dashboard"
                >
                  Accéder au dashboard
                </Button>
              </div>
            )}

            {status === 'error' && (
              <div data-testid="payment-error">
                <XCircle className="w-16 h-16 text-destructive mx-auto mb-4" />
                <h2 className="text-2xl font-heading font-semibold mb-2">Échec de la vérification</h2>
                <p className="text-muted mb-6">
                  Nous n'avons pas pu vérifier votre paiement. Si vous avez été débité, contactez le support.
                </p>
                <div className="flex gap-4 justify-center">
                  <Button
                    variant="outline"
                    onClick={() => checkPaymentStatus()}
                    data-testid="retry-button"
                  >
                    Réessayer
                  </Button>
                  <Button
                    onClick={() => navigate('/dashboard')}
                    data-testid="back-to-dashboard"
                  >
                    Retour au dashboard
                  </Button>
                </div>
              </div>
            )}
          </Card>
        </motion.div>
      </div>
    </div>
  );
};

export default PaymentSuccess;